<template>
  <div :class="{'printmode':isPrinting}" class="container py-5 mx-auto">
    <h1>LocalDeck Configurator</h1>
    <div class="form-control">
      <input v-model="editor.title"
             class="input border-secondary inline-block"
             pattern="[a-zA-Z0-9]+"
             placeholder="Project Name"
             type="text"
      />
      <button class="btn btn-primary" @click="save">Download</button>
      <button class="btn btn-error" @click="resetting=true">Reset</button>
      <button class="btn btn-secondary" @click="print">Print</button>
    </div>
    <div class="flex flex-wrap justify-center gap-10">

      <div>
        <h2>GUI</h2>
        <DeckPad v-if="editor" v-model="editor" v-model:editing="editing" class="mx-auto"/>
      </div>

      <DeckButtonConfig v-if="editing" v-model="editing"/>
      <div v-else class="basis-2 grow"></div>
    </div>


    <RippleUiModal v-model="resetting" title="Are you sure?">
      <p>
        This will reset all button configurations to their default values.<br/>
        It will <b>not</b> save your changes by itself, so you will need to click the save button after resetting!
      </p>
      <div class="flex justify-end gap-2">
        <button class="btn btn-outline-error" @click="reset">Reset</button>
        <button class="btn btn-primary" @click="resetting=false">Cancel</button>
      </div>
    </RippleUiModal>
  </div>
</template>
<script lang="ts" setup>
import {watchDebounced} from "@vueuse/core";
import newConfig from "~~/packages/localdeck-config/packages/localdeck-codegen/dist/esphome-localdeck";
import type {EditContainer} from "~~/packages/localdeck-config/packages/localdeck-components/src/utils/PadCfg";
import {ConfiguredButton} from "~~/packages/localdeck-config/packages/localdeck-codegen/dist/virtuals";

const route = useRoute();
const router = useRouter();

const config = new ConfigUtil();
if (route.query.config) config.setChanges(decompress(route.query.config as string));
config.notify = () => triggerRef(editor);

const resetting = ref(false);
const editor = ref(config.editor());
const editing = ref<EditContainer>();

const isPrinting = computed(() => Boolean(route.query.print));
provide(isPrintingSymbol, isPrinting);

watchDebounced(editor, () => {
  let compressed = compress(config.getChanges());
  router.replace({query: {...route.query, config: compressed}});
}, {deep: true, debounce: 250});

watch(isPrinting, ((isPrinting) => {
  if (!import.meta.client) return;
  setTimeout(() => setPageLayout(isPrinting ? "printable" : "default"), 100);
  if (isPrinting) setTimeout(() => window.print(), 500);
}), {
  immediate: true
});
const print = async () => {
  let compressed = compress(config.getChanges());
  await router.push({query: {config: compressed, print: 1}});
}

const reset = () => {
  if (!confirm("Are you sure you want to reset?")) return;
  config.resetChanges();
  resetting.value = false;
}

const save = () => {
  let {config: yamlConfig} = newConfig();
  Object.entries(editor.value.buttons).forEach(([num, b]) => yamlConfig.addComponent(new ConfiguredButton(b)));

  let fileContent = "# This file was generated by the LocalBytes LocalDeck Configurator\n";
  fileContent += `\n# Edit: ${getEditorUrl(config.getChanges())}\n\n`
  fileContent += yamlConfig.synthYaml();

  let blob = new Blob([fileContent], {type: "application/yaml"});
  let url = URL.createObjectURL(blob);
  let a = document.createElement("a");
  a.href = url;
  a.download = "localdeck.yaml";
  a.click();
  URL.revokeObjectURL(url);
}

definePageMeta({
  title: "LocalDeck Configurator",
  description: "Configure your LocalDeck for your needs!",
})

</script>
